'use client';

import React, { useState, useEffect, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  href: string;
}

interface ServiceItem {
  number: string;
  title: string;
  description: string;
  image: string;
  alt: string;
}

interface WhyItem {
  number: string;
  title: string;
  description: string;
}

interface IndustryItem {
  name: string;
  image: string;
  alt: string;
}

interface StatItem {
  value: string;
  label: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const NAV_ITEMS: NavItem[] = [
{ label: 'HOME', href: '#home' },
{ label: 'ABOUT', href: '#about' },
{ label: 'SERVICES', href: '#services' },
{ label: 'QUALITY', href: '#capabilities' },
{ label: 'CONTACT', href: '#contact' }];


const HERO_SLIDES = [
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd63c188-1772895408017.png",
  alt: 'Dallas area CNC machine shop facility interior with precision machining equipment'
},
{
  src: 'https://ftcindustries.com/wp-content/uploads/2019/07/CNC-Turning-Services.jpg',
  alt: 'CNC turning services at FTC Industries showing lathe machining precision parts'
}];


const SERVICES: ServiceItem[] = [
{
  number: '- 01',
  title: 'CNC TURNING',
  description:
  'Precision CNC turning services for complex cylindrical and contoured parts. We deliver tight tolerances and consistent quality across prototype and production volumes.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cda945ec-1771891856301.png",
  alt: 'CNC turning service at FTC Industries machine shop showing lathe cutting a metal part'
},
{
  number: '- 02',
  title: 'CNC MILLING',
  description:
  'Multi-axis CNC milling for intricate geometries and demanding specifications. From simple prismatic parts to complex 3D profiles, we machine it right the first time.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bef049ef-1781187683672.png",
  alt: 'CNC milling service near Dallas showing multi-axis milling machine cutting aluminum part'
},
{
  number: '- 03',
  title: 'HIGH/LOW VOLUME MANUFACTURING',
  description:
  'Scalable manufacturing solutions from single prototypes to high-volume production runs. Our flexible capacity adapts to your schedule and quantity requirements.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_152bb75ff-1781187687327.png",
  alt: 'DFW machining services facility showing high volume CNC manufacturing production floor'
},
{
  number: '- 04',
  title: 'WELDING & PARTS FINISHING',
  description:
  'Full-service welding, assembly, and parts finishing under one roof. We handle deburring, anodizing coordination, painting, and final assembly to deliver complete components.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_109f17b4c-1772129740193.png",
  alt: 'Custom machined parts with finishing showing completed precision components ready for delivery'
}];


const WHY_ITEMS: WhyItem[] = [
{
  number: '- 01',
  title: 'SHORT LEAD TIMES',
  description:
  'We understand that your production schedule cannot wait. Our streamlined processes and dedicated team deliver parts faster than the competition — same-day quotes available.'
},
{
  number: '- 02',
  title: 'COMPETITIVE PRICING',
  description:
  'Efficient operations and modern equipment allow us to offer industry-leading pricing without sacrificing quality. Get a same-day quote and see the FTC difference.'
},
{
  number: '- 03',
  title: 'WAREHOUSING & LOGISTICS',
  description:
  'Flexible warehousing and logistics options to support your supply chain. We manage inventory and coordinate delivery so you can focus on your business.'
},
{
  number: '- 04',
  title: 'FLEXIBLE CAPACITY',
  description:
  'From one-off prototypes to large production runs, our scalable capacity meets your needs. We adapt to your project size, timeline, and specification requirements.'
},
{
  number: '- 05',
  title: 'SAME DAY QUOTES',
  description:
  "Need a price fast? We provide same-day quotes on most projects. Send us your drawings and specifications and we'll respond with a competitive quote the same business day."
},
{
  number: '- 06',
  title: 'CONSISTENT QUALITY',
  description:
  'FTC Industries is known for consistent, quality machined parts. Every component is inspected before it ships — because your reputation depends on parts that perform.'
}];


const INDUSTRIES: IndustryItem[] = [
{ name: 'AEROSPACE', image: 'https://ftcindustries.com/wp-content/uploads/2019/07/Aerospace.jpg', alt: 'Aerospace machining services showing precision aerospace components manufactured at FTC Industries' },
{ name: 'DEFENSE', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1df983d12-1772127176812.png", alt: 'Military and defense machining services showing precision defense components' },
{ name: 'OIL & GAS', image: "https://img.rocket.new/generatedImages/rocket_gen_img_178f333e2-1773380308793.png", alt: 'Oil and gas industry machining services showing precision components for energy sector' },
{ name: 'MEDICAL', image: "https://img.rocket.new/generatedImages/rocket_gen_img_136c59bcc-1768889699978.png", alt: 'Medical device machining services showing precision medical components manufactured to tight tolerances' },
{ name: 'COMMERCIAL', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1df983d12-1772127176812.png", alt: 'Commercial machining services showing precision commercial components and assemblies' },
{ name: 'INDUSTRIAL', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efc2d80f-1781187149516.png", alt: 'Industrial CNC machining in Dallas showing heavy industrial components being machined' }];



const STATS: StatItem[] = [
{ value: '43K', label: 'SQ FT FACILITY' },
{ value: 'DFW', label: 'DALLAS-FORT WORTH' },
{ value: '100%', label: 'U.S. SHIPPING' },
{ value: 'SAME', label: 'DAY QUOTES' }];


const GALLERY_IMAGES = [
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f39db5d6-1773321253369.png", alt: 'Dallas machine shop interior showing CNC machining equipment and production floor' },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad227694-1766493775758.png", alt: 'Fort Worth CNC machine shop showing precision machining operations in progress' },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e22091db-1768738560903.png", alt: 'Dallas CNC machining showing close-up of CNC machine cutting metal parts' },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1bedcc220-1779007375842.png", alt: 'Custom machined parts showing finished precision components ready for quality inspection' },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_11aba98ac-1766506477520.png", alt: 'Machined parts inspection showing quality control process with precision measurement tools' },
{ src: 'https://ftcindustries.com/wp-content/uploads/2019/07/Dallas-machine-shops.png', alt: 'Dallas machine shops overview showing FTC Industries facility and machining capabilities' }];


// ─── Utility ─────────────────────────────────────────────────────────────────
function useIntersection(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function TopBar() {
  return (
    <div style={{ backgroundColor: '#4F190B' }} className="w-full py-2 px-4">
      <div
        className="max-w-7xl mx-auto flex items-center justify-between text-xs"
        style={{ color: '#839595' }}>
        <div className="flex items-center gap-4 flex-wrap">
          <a
            href="tel:+18173850021"
            className="hover:text-white transition-colors duration-200"
            style={{ color: '#839595' }}>
            817-385-0021
          </a>
          <span className="hidden sm:inline">—</span>
          <span className="hidden sm:inline">728 111th St., Arlington, TX 76011</span>
          <span className="hidden sm:inline">—</span>
          <a
            href="mailto:rick.flores@ftcindustries.com"
            className="hover:text-white transition-colors duration-200"
            style={{ color: '#839595' }}>
            rick.flores@ftcindustries.com
          </a>
        </div>
        <div
          className="text-xs tracking-widest font-fjalla uppercase hidden md:block"
          style={{ color: '#839595' }}>
          DALLAS-FORT WORTH MACHINE SHOP
        </div>
      </div>
    </div>);

}

function Navbar({ scrolled }: {scrolled: boolean;}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    scrollToId(href.replace('#', ''));
  };

  return (
    <nav
      className="w-full z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.12)' : 'none'
      }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => handleNav('#home')}
          className="flex items-center gap-3 focus:outline-none">
          <img
            src="/assets/images/Logo-vector-1781187810352.png"
            alt="FTC Industries logo - Dallas Fort Worth CNC machine shop"
            style={{ height: '40px', width: 'auto' }}
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }} />
          
          <span
            className="font-fjalla uppercase tracking-widest hidden items-center gap-1"
            style={{ color: scrolled ? '#B72A2A' : '#ffffff', fontSize: '22px', display: 'none' }}>
            <span style={{ color: '#B72A2A' }}>FTC</span>
            <span style={{ color: scrolled ? '#000000' : '#ffffff', fontSize: '14px', marginLeft: '4px' }}>INDUSTRIES</span>
          </span>
        </button>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) =>
          <li key={item.label}>
              <button
              onClick={() => handleNav(item.href)}
              className="font-fjalla text-sm tracking-wider transition-colors duration-200 hover:opacity-70 focus:outline-none"
              style={{ color: scrolled ? '#B72A2A' : '#ffffff' }}>
                {item.label}
              </button>
            </li>
          )}
        </ul>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 focus:outline-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu">
          <span
            className="block w-6 h-0.5 transition-all duration-300"
            style={{
              backgroundColor: '#B72A2A',
              transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none'
            }} />
          
          <span
            className="block w-6 h-0.5 transition-all duration-300"
            style={{ backgroundColor: '#B72A2A', opacity: menuOpen ? 0 : 1 }} />
          
          <span
            className="block w-6 h-0.5 transition-all duration-300"
            style={{
              backgroundColor: '#B72A2A',
              transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none'
            }} />
          
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen &&
      <div className="md:hidden px-6 pb-4" style={{ backgroundColor: 'rgba(255,255,255,0.97)' }}>
          <ul className="flex flex-col gap-4">
            {NAV_ITEMS.map((item) =>
          <li key={item.label}>
                <button
              onClick={() => handleNav(item.href)}
              className="font-fjalla text-sm tracking-wider w-full text-left focus:outline-none"
              style={{ color: '#B72A2A' }}>
                  {item.label}
                </button>
              </li>
          )}
          </ul>
        </div>
      }
    </nav>);

}

function HeroSection() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" className="relative w-full overflow-hidden" style={{ minHeight: '100vh' }}>
      {/* Slideshow backgrounds */}
      {HERO_SLIDES.map((slide, idx) =>
      <div
        key={slide.src}
        className="absolute inset-0 transition-opacity duration-1000"
        style={{ opacity: activeSlide === idx ? 1 : 0 }}>
          <div
          className="absolute inset-0 animate-kenburns"
          style={{
            backgroundImage: `url(${slide.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transformOrigin: 'center center'
          }} />
        </div>
      )}

      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(30,20,15,0.60)', zIndex: 1 }} />
      

      {/* Dot overlay pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
          'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          zIndex: 2
        }} />
      

      {/* Content */}
      <div
        className="relative flex flex-col justify-center max-w-7xl mx-auto px-6"
        style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '80px', zIndex: 3 }}>
        <div className="max-w-2xl">
          <h1
            className="font-fjalla uppercase leading-tight mb-4"
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              color: '#B72A2A',
              letterSpacing: '3px',
              lineHeight: 1.1
            }}>
            Dallas-Fort Worth CNC Machining Services
          </h1>
          <h2
            className="font-fjalla uppercase mb-6"
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '2px'
            }}>
            CNC TURNING | CNC MILLING | MANUFACTURING | ASSEMBLY
          </h2>
          <div className="w-16 mb-6" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
          <p
            className="font-fjalla text-sm mb-8 flex items-center gap-2"
            style={{ color: 'rgba(255,255,255,0.75)', letterSpacing: '1px' }}>
            <span style={{ color: '#B72A2A', fontSize: '18px' }}>↪</span>
            Come Visit Our New 43,000 Square Foot Facility!
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollToId('contact')}
              className="font-fjalla uppercase tracking-widest text-sm px-8 py-3 transition-all duration-300 hover:opacity-80"
              style={{
                backgroundColor: '#B72A2A',
                color: '#ffffff',
                border: '1px solid #B72A2A',
                borderRadius: '4px'
              }}>
              GET A FREE QUOTE
            </button>
            <button
              onClick={() => scrollToId('services')}
              className="font-fjalla uppercase tracking-widest text-sm px-8 py-3 transition-all duration-300 hover:bg-white/10"
              style={{
                backgroundColor: 'transparent',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.6)',
                borderRadius: '4px'
              }}>
              OUR SERVICES
            </button>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-10 left-6 flex gap-2" style={{ zIndex: 4 }}>
          {HERO_SLIDES.map((_, idx) =>
          <button
            key={idx}
            onClick={() => setActiveSlide(idx)}
            className="transition-all duration-300 focus:outline-none"
            style={{
              width: activeSlide === idx ? '24px' : '8px',
              height: '3px',
              backgroundColor: activeSlide === idx ? '#B72A2A' : 'rgba(255,255,255,0.4)',
              border: 'none',
              padding: 0
            }}
            aria-label={`Go to slide ${idx + 1}`} />

          )}
        </div>
      </div>
    </section>);

}

function CTABannerSection() {
  const { ref, visible } = useIntersection();
  return (
    <section style={{ backgroundColor: '#7A7A7A', padding: '40px 0' }}>
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center gap-8">
        <div
          className="flex-1"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <h3
            className="font-fjalla uppercase mb-3"
            style={{ fontSize: '28px', color: '#ffffff', fontWeight: 900 }}>
            LOOKING FOR A BETTER MACHINE SHOP?
          </h3>
          <p
            className="font-fjalla text-sm mb-4"
            style={{ color: 'rgba(255,255,255,0.8)', maxWidth: '520px', lineHeight: 1.7 }}>
            FTC Industries is a leading Dallas / Fort Worth area machine shop. We build custom
            machined parts in Arlington, Texas and ship to companies across the U.S.
          </p>
          <a
            href="https://ftcindustries.com/choosing-the-right-machine-shop"
            target="_blank"
            rel="noopener noreferrer"
            className="font-fjalla uppercase tracking-wider text-xs px-6 py-3 transition-all duration-300 hover:opacity-80 flex items-center gap-2 inline-flex"
            style={{
              backgroundColor: 'rgba(105,114,125,0.8)',
              color: '#ffffff',
              border: '1px solid #ffffff',
              borderRadius: '6px'
            }}>
            <span style={{ color: '#B72A2A' }}>→</span>
            CHECK OUT OUR GUIDE TO SELECTING A MACHINE SHOP
          </a>
        </div>

        <ul
          className="flex flex-col gap-2"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(30px)',
            transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s'
          }}>
          {[
          'Short Lead Times',
          'Competitive Pricing',
          'Warehousing / Logistics Options',
          'Flexible — Prototypes to Production Runs',
          'Same Day Quotes'].
          map((item) =>
          <li
            key={item}
            className="font-fjalla text-sm flex items-center gap-2"
            style={{ color: 'rgba(255,255,255,0.85)' }}>
              <span style={{ color: '#B72A2A', fontSize: '16px' }}>✓</span>
              {item}
            </li>
          )}
        </ul>
      </div>
    </section>);

}

function AboutSection() {
  const { ref, visible } = useIntersection();
  return (
    <section id="about" style={{ backgroundColor: '#ffffff', padding: '100px 0 0' }}>
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-start">
        {/* Left: image collage + stats */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}>
          {/* Main facility image */}
          <img
            src="https://ftcindustries.com/wp-content/uploads/2019/08/Dallas-machine-shop.jpg"
            alt="Dallas machine shop interior showing FTC Industries CNC machining facility"
            className="w-full object-cover"
            style={{ aspectRatio: '16/9' }} />
          
          {/* Two smaller images below */}
          <div className="grid grid-cols-2 gap-0">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1ad227694-1766493775758.png"
              alt="Fort Worth CNC machine shop showing precision machining operations"
              className="w-full object-cover"
              style={{ height: '160px' }} />
            <img
              src="https://images.unsplash.com/photo-1697071628662-6e14d952d488"
              alt="Dallas CNC machining showing close-up of CNC machine in operation"
              className="w-full object-cover"
              style={{ height: '160px' }} />
          </div>

          <div className="grid grid-cols-2 gap-0" style={{ backgroundColor: '#4F190B' }}>
            {STATS.map((stat, i) =>
            <div
              key={stat.label}
              className="p-6 text-center"
              style={{
                borderRight: i % 2 === 0 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.1)' : 'none'
              }}>
                <div className="font-fjalla text-3xl mb-1" style={{ color: '#B72A2A' }}>
                  {stat.value}
                </div>
                <div
                className="font-fjalla text-xs tracking-widest"
                style={{ color: '#839595' }}>
                  {stat.label}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: content */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-3"
            style={{ color: '#839595' }}>
            ARLINGTON, TEXAS MACHINE SHOP
          </p>
          <h2
            className="font-fjalla uppercase mb-6"
            style={{
              fontSize: 'clamp(36px, 4vw, 60px)',
              color: '#000000',
              lineHeight: 1.05
            }}>
            ABOUT US
          </h2>
          <div className="w-12 mb-6" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
          <p
            className="font-fjalla text-sm mb-6 leading-relaxed"
            style={{ color: '#627066' }}>
            FTC Industries is a leading Dallas / Fort Worth area machine shop. We build custom
            machined parts in Arlington, Texas and ship to companies across the U.S. Operating
            from our new 43,000 square foot facility, we deliver consistent, quality machined
            parts with short lead times and competitive pricing.
          </p>
          <p
            className="font-fjalla text-sm mb-8 leading-relaxed"
            style={{ color: '#627066' }}>
            Founded in 1997, the leadership team at FTC Industries has committed to growth
            through out-hustling and out-servicing the other machine shops — not just in Dallas,
            Fort Worth, or the State of Texas, but nationwide. Our new 43,000 sq ft
            climate-controlled facility is 15 minutes south of DFW International Airport.
          </p>

          <h3
            className="font-fjalla uppercase text-base mb-3"
            style={{ color: '#000000' }}>
            Our Services Include:
          </h3>
          <ul className="grid grid-cols-2 gap-2 mb-8">
            {[
            'CNC Turning',
            'CNC Milling',
            'High/Low Volume Manufacturing',
            'Welding',
            'Parts Finishing',
            'Mechanical Design Support'].
            map((item) =>
            <li
              key={item}
              className="font-fjalla text-xs flex items-center gap-2"
              style={{ color: '#627066' }}>
                <span style={{ color: '#B72A2A' }}>▸</span>
                {item}
              </li>
            )}
          </ul>

          <div
            className="p-5"
            style={{
              backgroundColor: 'rgba(131,149,149,0.08)',
              borderLeft: '3px solid #9E0000'
            }}>
            <h4
              className="font-fjalla uppercase mb-2"
              style={{ fontSize: '18px', color: '#9E0000' }}>
              Need a Same-Day Quote?
            </h4>
            <p className="font-fjalla text-sm" style={{ color: '#627066' }}>
              Contact us today for a same-day quote. We respond fast and deliver faster.
              Call us at 817-385-0021 or email rick.flores@ftcindustries.com.
            </p>
          </div>
        </div>
      </div>
    </section>);

}

function ServicesSection() {
  const { ref, visible } = useIntersection();
  return (
    <section
      id="services"
      className="relative"
      style={{ padding: '150px 0 100px' }}>
      {/* Tilt shape divider top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden" style={{ height: '80px' }}>
        <svg
          viewBox="0 0 1200 80"
          preserveAspectRatio="none"
          className="w-full h-full"
          style={{ display: 'block' }}>
          <polygon points="0,0 1200,0 1200,80" fill="#ffffff" />
        </svg>
      </div>

      {/* Background image with dark overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
          "https://img.rocket.new/generatedImages/rocket_gen_img_1224a2152-1780141830948.png",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }} />
      
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(20,15,10,0.82)' }} />
      

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-2"
            style={{ color: 'rgba(255,255,255,0.5)' }}>
            WHAT WE DO
          </p>
          <h2
            className="font-fjalla uppercase"
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              color: '#ffffff',
              lineHeight: 1.05
            }}>
            SERVICES
          </h2>
          <div className="w-12 mt-4 mb-6" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
          <p
            className="font-fjalla uppercase text-sm"
            style={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '2px' }}>
            CNC TURNING | CNC MILLING | HIGH/LOW VOLUME MANUFACTURING | WELDING | PARTS FINISHING | MECHANICAL DESIGN SUPPORT
          </p>
        </div>

        {/* Service items with images */}
        <div className="grid md:grid-cols-2 gap-0">
          {SERVICES.map((service, idx) =>
          <div
            key={service.number}
            className="group cursor-default transition-all duration-300"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 0.7s ease ${0.1 * idx + 0.3}s, transform 0.7s ease ${0.1 * idx + 0.3}s`
            }}>
            {/* Service image */}
            <div className="relative overflow-hidden" style={{ height: '200px' }}>
              <img
                src={service.image}
                alt={service.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              
              <div
                className="absolute inset-0"
                style={{ backgroundColor: 'rgba(20,15,10,0.45)' }} />
              <div className="absolute bottom-3 left-4">
                <p className="font-fjalla text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {service.number}
                </p>
              </div>
            </div>
            {/* Service text */}
            <div
              className="p-8 hover:bg-white/5"
              style={{
                borderTop: '1px solid rgba(255,255,255,0.1)',
                borderLeft: idx % 2 === 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
              }}>
              <h4
                className="font-fjalla uppercase mb-3 group-hover:text-red-400 transition-colors duration-300"
                style={{ fontSize: '28px', color: '#ffffff' }}>
                {service.title}
              </h4>
              <div className="w-8 mb-4" style={{ height: '2px', backgroundColor: '#B72A2A' }} />
              <p
                className="font-fjalla text-sm leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.57)' }}>
                {service.description}
              </p>
            </div>
          </div>
          )}
        </div>

        {/* Bottom tagline */}
        <div
          className="mt-16 text-center"
          style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.7s ease 0.7s' }}>
          <p
            className="font-fjalla uppercase"
            style={{
              fontSize: 'clamp(14px, 2vw, 22px)',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '2px'
            }}>
            FTC IS KNOWN FOR OUR CONSISTENT, QUALITY MACHINED PARTS, SHORT LEAD TIMES, AND COMPETITIVE PRICING.
          </p>
        </div>
      </div>
    </section>);

}

function CapabilitiesSection() {
  const { ref, visible } = useIntersection();
  return (
    <section id="capabilities" style={{ backgroundColor: '#ffffff', padding: '100px 0 0 0' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className="mb-12"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-2"
            style={{ color: '#839595' }}>
            INDUSTRY
          </p>
          <h2
            className="font-fjalla uppercase"
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              color: '#000000',
              lineHeight: 1.05
            }}>
            EXPERIENCE
          </h2>
          <div className="w-12 mt-4 mb-6" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
          <p
            className="font-fjalla text-sm max-w-2xl"
            style={{ color: '#627066', lineHeight: 1.8 }}>
            FTC Industries serves clients across the most demanding industries. Our team
            understands the unique requirements, certifications, and tolerances each sector
            demands — delivering parts that meet spec, every time.
          </p>
        </div>

        {/* Industry grid with actual photos */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0 mb-16">
          {INDUSTRIES.map((industry, idx) =>
          <div
            key={industry.name}
            className="relative overflow-hidden group cursor-default"
            style={{
              height: '220px',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: `opacity 0.6s ease ${0.1 * idx + 0.2}s, transform 0.6s ease ${0.1 * idx + 0.2}s`
            }}>
            <img
              src={industry.image}
              alt={industry.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            
            <div
              className="absolute inset-0 transition-all duration-300"
              style={{ backgroundColor: 'rgba(79,25,11,0.55)' }} />
            
            <div className="absolute inset-0 flex items-end p-4">
              <p
                className="font-fjalla uppercase text-sm tracking-widest"
                style={{ color: '#ffffff' }}>
                {industry.name}
              </p>
            </div>
          </div>
          )}
        </div>

        {/* Full-width facility image */}
        <div
          className="relative overflow-hidden"
          style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease 0.5s' }}>
          <img
            src="https://ftcindustries.com/wp-content/uploads/2019/08/DFW-machining-services-1024x476.jpg"
            alt="FTC Industries 43,000 square foot CNC machining facility in Arlington, Texas showing full production floor"
            className="w-full object-cover"
            style={{ height: '400px' }} />
          
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(79,25,11,0.65)' }}>
            <div className="text-center px-6">
              <p
                className="font-fjalla uppercase tracking-widest text-sm mb-3"
                style={{ color: 'rgba(255,255,255,0.6)' }}>
                OUR FACILITY
              </p>
              <h3
                className="font-fjalla uppercase mb-4"
                style={{ fontSize: 'clamp(24px, 3vw, 40px)', color: '#ffffff' }}>
                43,000 SQ FT. ZERO COMPROMISES.
              </h3>
              <p
                className="font-fjalla text-sm"
                style={{
                  color: 'rgba(255,255,255,0.7)',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}>
                CNC turning, CNC milling, welding, parts finishing, and mechanical design
                support — all under one roof in Arlington, Texas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function GallerySection() {
  const { ref, visible } = useIntersection();
  return (
    <section style={{ backgroundColor: '#ffffff', padding: '80px 0' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div
          className="mb-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-2"
            style={{ color: '#839595' }}>
            INSIDE FTC
          </p>
          <h2
            className="font-fjalla uppercase"
            style={{
              fontSize: 'clamp(28px, 4vw, 48px)',
              color: '#000000',
              lineHeight: 1.05
            }}>
            PHOTO GALLERY
          </h2>
          <div className="w-12 mt-4" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {GALLERY_IMAGES.map((img, idx) =>
          <div
            key={img.src}
            className="relative overflow-hidden group"
            style={{
              height: '220px',
              opacity: visible ? 1 : 0,
              transform: visible ? 'scale(1)' : 'scale(0.95)',
              transition: `opacity 0.6s ease ${0.08 * idx + 0.1}s, transform 0.6s ease ${0.08 * idx + 0.1}s`
            }}>
              <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            
              <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(79,25,11,0.5)' }}>
                <span className="font-fjalla text-white text-xs tracking-widest uppercase">VIEW</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

function WhySection() {
  const { ref, visible } = useIntersection();
  return (
    <section
      className="relative"
      style={{ padding: '150px 0 100px' }}>
      {/* Tilt shape divider top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden" style={{ height: '80px' }}>
        <svg
          viewBox="0 0 1200 80"
          preserveAspectRatio="none"
          className="w-full h-full"
          style={{ display: 'block' }}>
          <polygon points="0,0 1200,0 0,80" fill="#ffffff" />
        </svg>
      </div>

      {/* Background: quality inspection photo */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
          "https://img.rocket.new/generatedImages/rocket_gen_img_1224a2152-1780141830948.png",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }} />
      
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(20,15,10,0.85)' }} />
      

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-2"
            style={{ color: 'rgba(255,255,255,0.5)' }}>
            THE FTC INDUSTRIES ADVANTAGE
          </p>
          <h2
            className="font-fjalla uppercase"
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              color: '#ffffff',
              lineHeight: 1.05
            }}>
            WHY FTC?
          </h2>
          <div className="w-12 mt-4" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
        </div>

        {/* Why items grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0">
          {WHY_ITEMS.map((item, idx) =>
          <div
            key={item.number}
            className="p-8 group cursor-default transition-all duration-300 hover:bg-white/5"
            style={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 0.7s ease ${0.1 * idx + 0.2}s, transform 0.7s ease ${0.1 * idx + 0.2}s`
            }}>
              <p className="font-fjalla text-sm mb-2" style={{ color: '#C6C6C6' }}>
                {item.number}
              </p>
              <h4
              className="font-fjalla uppercase mb-3 group-hover:text-red-400 transition-colors duration-300"
              style={{ fontSize: '28px', color: '#ffffff' }}>
                {item.title}
              </h4>
              <div className="w-8 mb-4" style={{ height: '2px', backgroundColor: '#B72A2A' }} />
              <p
              className="font-fjalla text-sm leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.57)' }}>
                {item.description}
              </p>
            </div>
          )}
        </div>

        {/* Quality badge */}
        <div
          className="mt-16 flex flex-col md:flex-row items-center gap-8 p-8"
          style={{
            border: '1px solid rgba(255,255,255,0.15)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.8s'
          }}>
          <div className="flex-shrink-0">
            <img
              src="https://ftcindustries.com/wp-content/uploads/2019/08/Machined-parts-inspection-2.jpg"
              alt="FTC Industries quality inspection process showing precision measurement of machined parts"
              className="object-cover"
              style={{ width: '200px', height: '140px' }} />
            
          </div>
          <div>
            <p className="font-fjalla uppercase tracking-widest text-xs mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>QUALITY COMMITMENT</p>
            <h4 className="font-fjalla uppercase text-xl mb-3" style={{ color: '#ffffff' }}>ISO 9000 COMPLIANT — PRECISION GUARANTEED</h4>
            <p className="font-fjalla text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Our rigorous quality standards ensure that you receive machined parts that meet your exact requirements.
              FTC Industries is ISO 9000 compliant and applies Statistical Process Controls on critical dimensions,
              ensuring parts are machined to the tightest tolerances with less scrap and waste.
            </p>
          </div>
        </div>
      </div>
    </section>);

}

function ContactSection() {
  const { ref, visible } = useIntersection();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" style={{ backgroundColor: '#ffffff', padding: '100px 0' }}>
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-start">
        {/* Left: info */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
          <p
            className="font-fjalla uppercase tracking-widest text-sm mb-3"
            style={{ color: '#839595' }}>
            HAVE A QUESTION?
          </p>
          <h2
            className="font-fjalla uppercase mb-6"
            style={{
              fontSize: 'clamp(36px, 4vw, 60px)',
              color: '#000000',
              lineHeight: 1.05
            }}>
            LET&apos;S TALK
          </h2>
          <div className="w-12 mb-6" style={{ height: '3px', backgroundColor: '#B72A2A' }} />
          <p
            className="font-fjalla text-sm mb-10 leading-relaxed"
            style={{ color: '#627066' }}>
            Whether you need a same-day price quote, or have a question about our machining
            capabilities, or just want to say &quot;hi&quot;, reach out to us today!
          </p>

          <div className="flex flex-col gap-6">
            <div>
              <p
                className="font-fjalla uppercase tracking-widest text-xs mb-1"
                style={{ color: '#839595' }}>
                OR CALL US AT
              </p>
              <a
                href="tel:+18173850021"
                className="font-fjalla transition-colors duration-200 hover:text-red-700"
                style={{ fontSize: '28px', color: '#000000' }}>
                +1 817 385 0021
              </a>
            </div>
            <div>
              <p
                className="font-fjalla uppercase tracking-widest text-xs mb-1"
                style={{ color: '#839595' }}>
                EMAIL
              </p>
              <a
                href="mailto:rick.flores@ftcindustries.com"
                className="font-fjalla text-sm transition-colors duration-200 hover:text-red-700"
                style={{ color: '#000000' }}>
                rick.flores@ftcindustries.com
              </a>
            </div>
            <div>
              <p
                className="font-fjalla uppercase tracking-widest text-xs mb-1"
                style={{ color: '#839595' }}>
                LOCATION
              </p>
              <p className="font-fjalla text-sm" style={{ color: '#000000' }}>
                FTC Industries
              </p>
              <p className="font-fjalla text-sm" style={{ color: '#000000' }}>
                728 111th St., Arlington, TX 76011
              </p>
            </div>
          </div>

          {/* Map/location image */}
          <div className="mt-8 relative overflow-hidden rounded" style={{ height: '220px' }}>
            <iframe
              src="https://maps.google.com/maps?q=728+111th+St,+Arlington,+TX+76011&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="220"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="FTC Industries location map - 728 111th St, Arlington TX 76011" />
            
          </div>
        </div>

        {/* Right: form */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(30px)',
            transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s'
          }}>
          {submitted ?
          <div
            className="p-10 text-center"
            style={{
              backgroundColor: 'rgba(131,149,149,0.08)',
              border: '1px solid #DADADA'
            }}>
              <div className="font-fjalla text-4xl mb-4" style={{ color: '#B72A2A' }}>
                ✓
              </div>
              <h3
              className="font-fjalla uppercase text-xl mb-2"
              style={{ color: '#000000' }}>
                MESSAGE SENT!
              </h3>
              <p className="font-fjalla text-sm" style={{ color: '#627066' }}>
                Thank you for reaching out. We&apos;ll get back to you within 1 business hour.
              </p>
            </div> :

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {[
            { id: 'name', label: 'YOUR NAME *', type: 'text', required: true },
            { id: 'email', label: 'EMAIL ADDRESS *', type: 'email', required: true },
            { id: 'phone', label: 'PHONE NUMBER', type: 'tel', required: false }].
            map((field) =>
            <div key={field.id}>
                  <label
                htmlFor={field.id}
                className="font-fjalla uppercase text-xs tracking-widest block mb-1"
                style={{ color: '#839595' }}>
                    {field.label}
                  </label>
                  <input
                id={field.id}
                type={field.type}
                required={field.required}
                value={formData[field.id as keyof typeof formData]}
                onChange={(e) =>
                setFormData((prev) => ({ ...prev, [field.id]: e.target.value }))
                }
                className="w-full font-fjalla text-sm px-4 py-3 outline-none transition-all duration-200 focus:border-red-700"
                style={{
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #DADADA',
                  color: '#424242'
                }} />
              
                </div>
            )}
              <div>
                <label
                htmlFor="message"
                className="font-fjalla uppercase text-xs tracking-widest block mb-1"
                style={{ color: '#839595' }}>
                  YOUR MESSAGE *
                </label>
                <textarea
                id="message"
                required
                rows={5}
                value={formData.message}
                onChange={(e) =>
                setFormData((prev) => ({ ...prev, message: e.target.value }))
                }
                className="w-full font-fjalla text-sm px-4 py-3 outline-none transition-all duration-200 focus:border-red-700 resize-none"
                style={{
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #DADADA',
                  color: '#424242'
                }} />
              
              </div>
              <button
              type="submit"
              className="font-fjalla uppercase tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:opacity-80 mt-2"
              style={{
                backgroundColor: '#B72A2A',
                color: '#ffffff',
                border: 'none'
              }}>
                SEND MESSAGE →
              </button>
            </form>
          }
        </div>
      </div>
    </section>);

}

function Footer() {
  return (
    <footer style={{ backgroundColor: '#4F190B' }}>
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-10">
        {/* Col 1: Phone */}
        <div>
          <p
            className="font-fjalla uppercase text-xs tracking-widest mb-3"
            style={{ color: '#839595' }}>
            Phone:
          </p>
          <p className="font-fjalla text-sm" style={{ color: '#839595' }}>
            (817) 385-0021 (main)
          </p>
        </div>

        {/* Col 2: Location */}
        <div>
          <p
            className="font-fjalla uppercase text-xs tracking-widest mb-3"
            style={{ color: '#839595' }}>
            Location:
          </p>
          <p className="font-fjalla text-sm" style={{ color: '#839595' }}>
            FTC Industries
          </p>
          <p className="font-fjalla text-sm" style={{ color: '#839595' }}>
            728 111th St.
          </p>
          <p className="font-fjalla text-sm mb-4" style={{ color: '#839595' }}>
            Arlington, TX 76011
          </p>
          <a
            href="https://ftcindustries.com/us-machine-shop/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-fjalla uppercase text-xs tracking-widest px-4 py-2 transition-all duration-200 hover:opacity-70 inline-block"
            style={{ border: '1px solid #839595', color: '#839595' }}>
            AREAS WE SERVE
          </a>
        </div>

        {/* Col 3: Email + Social */}
        <div>
          <p
            className="font-fjalla uppercase text-xs tracking-widest mb-3"
            style={{ color: '#839595' }}>
            Email:
          </p>
          <a
            href="mailto:rick.flores@ftcindustries.com"
            className="font-fjalla text-sm mb-6 block hover:text-white transition-colors duration-200"
            style={{ color: '#839595' }}>
            rick.flores@ftcindustries.com
          </a>
          <div className="flex gap-3 mt-4">
            <a
              href="https://www.facebook.com/ftcindustriesinc/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center transition-all duration-200 hover:bg-white/10"
              style={{ border: '1px solid #839595' }}>
              <span className="font-fjalla text-xs" style={{ color: '#839595' }}>FB</span>
            </a>
            <a
              href="https://www.linkedin.com/company/ftc-industries-inc"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center transition-all duration-200 hover:bg-white/10"
              style={{ border: '1px solid #839595' }}>
              <span className="font-fjalla text-xs" style={{ color: '#839595' }}>LI</span>
            </a>
            <a
              href="https://g.page/DallasMachineShop?gm"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center transition-all duration-200 hover:bg-white/10"
              style={{ border: '1px solid #839595' }}>
              <span className="font-fjalla text-xs" style={{ color: '#839595' }}>G</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ backgroundColor: '#DADADA' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-fjalla text-xs" style={{ color: '#424242' }}>
            Copyright © 2023 FTC Industries | All Rights Reserved
          </p>
          <nav>
            <ul className="flex flex-wrap gap-6">
              {NAV_ITEMS.map((item) =>
              <li key={item.label}>
                  <button
                  onClick={() => scrollToId(item.href.replace('#', ''))}
                  className="font-fjalla text-xs tracking-wider transition-colors duration-200 hover:text-red-700 focus:outline-none"
                  style={{ color: '#424242' }}>
                    {item.label}
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </footer>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ fontFamily: '"Fjalla One", sans-serif', backgroundColor: '#ffffff' }}>
      {/* Sticky header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <TopBar />
        <Navbar scrolled={scrolled} />
      </div>

      {/* Page content */}
      <main>
        <HeroSection />
        <CTABannerSection />
        <AboutSection />
        <ServicesSection />
        <CapabilitiesSection />
        <GallerySection />
        <WhySection />
        <ContactSection />
      </main>

      <Footer />
    </div>);

}